import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { MODEL_CHOOSER_OPTIONS, TModelType } from "@/zapi-frontend/shared/types/models";
import { AnalysisDropdown } from "@/zapi-frontend/widgets/CreateProjectForm/ui/AnalysisDropdown";
import { ProjectErrors, TProjectDataWithoutRepository } from "@/zapi-frontend/widgets/CreateProjectForm/model/types";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";

type TAnalysisSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    projectData: TProjectDataWithoutRepository;
    selectedModel: TModelType;
    skipPrefill: boolean;
  }) => void;
  projectData: TProjectDataWithoutRepository;
  errors: ProjectErrors;
  setErrors: (errors: ProjectErrors) => void;
  selectedModel: TModelType;
  skipPrefill: boolean;
};

export function AnalysisSettingsModal({
  isOpen,
  onClose,
  onSave,
  projectData,
  errors,
  setErrors,
  selectedModel,
  skipPrefill
}: TAnalysisSettingsModalProps) {
  const [draftProjectData, setDraftProjectData] = useState<TProjectDataWithoutRepository>(projectData);
  const [draftModel, setDraftModel] = useState<TModelType>(selectedModel);
  const [draftSkipPrefill, setDraftSkipPrefill] = useState(skipPrefill);
  // Claude-code-generation projects have no template, so the analysis type picker
  // (and its per-chapter instructions) is a legacy-only control.
  const claudeGeneration = useFeatureFlag("claude-code-generation");

  useEffect(() => {
    if (!isOpen) return;
    setDraftProjectData(projectData);
    setDraftModel(selectedModel);
    setDraftSkipPrefill(skipPrefill);
  }, [isOpen, projectData, selectedModel, skipPrefill]);

  const updateErrors = (updates: Partial<ProjectErrors>) => {
    setErrors({ ...errors, ...updates });
  };

  const handleSave = () => {
    onSave({
      projectData: draftProjectData,
      selectedModel: draftModel,
      skipPrefill: draftSkipPrefill
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[480px]" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>
            <FigmaTypography variant="heading-3" color="zinc900">
              Analysis Settings
            </FigmaTypography>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 min-w-0">
          {claudeGeneration === false && (
            <AnalysisDropdown
              value={draftProjectData.projectTemplateSlug}
              onChange={(slug) => {
                setDraftProjectData({ ...draftProjectData, projectTemplateSlug: slug });
                updateErrors({ projectTemplateSlug: "" });
              }}
              error={errors.projectTemplateSlug}
              projectInstructions={draftProjectData.projectInstructions || ""}
              chaptersInstructions={draftProjectData.chaptersInstructions || {}}
              presentationPrompt={draftProjectData.presentationPrompt || ""}
              onInstructionsChange={(projectInstructions, chaptersInstructions) => {
                setDraftProjectData({ ...draftProjectData, projectInstructions, chaptersInstructions });
              }}
              onPresentationPromptChange={(value) => {
                setDraftProjectData({ ...draftProjectData, presentationPrompt: value });
              }}
            />
          )}

          <div className="flex flex-col gap-1.5">
            <FigmaTypography variant="label-2" color="zinc900">
              Generation Model
            </FigmaTypography>
            <Select value={draftModel} onValueChange={(value) => setDraftModel(value as TModelType)}>
              <SelectTrigger className="h-9 text-[14px]" data-testid="analysis-settings-model-select-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODEL_CHOOSER_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Checkbox
                id="skip-prefill-modal"
                checked={draftSkipPrefill}
                onCheckedChange={(checked) => setDraftSkipPrefill(checked === true)}
                data-testid="analysis-settings-skip-prefill-checkbox"
              />
              <label htmlFor="skip-prefill-modal" className="cursor-pointer">
                <FigmaTypography variant="label-2" color="zinc900">
                  {claudeGeneration ? "Skip analysis generation" : "Skip chapter generation"}
                </FigmaTypography>
              </label>
            </div>
            <FigmaTypography variant="body-3" color="zinc500">
              {claudeGeneration
                ? "Note: Documents will still be parsed even if analysis generation is skipped."
                : "Note: Documents will still be parsed and business models generated even if chapter generation is skipped."}
            </FigmaTypography>
          </div>
        </div>

        <DialogFooter>
          <AppButton
            appearance="outlined"
            color="zinc150"
            onClick={onClose}
            data-testid="analysis-settings-cancel-button"
          >
            Cancel
          </AppButton>
          <AppButton onClick={handleSave} data-testid="analysis-settings-save-button">
            Save Changes
          </AppButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
